// Zones (§31 D2): rectangles of the map, each with its own deposit character.
//
// **A zone is a label plus a spawn.** The label (`IndustryState.zone`) is
// terrain nothing in the rules reads yet — the client tints it, D3 grows the
// board by it, A1 spawns sparks in it. The spawn is ordinary D1 material:
// mixed tiered deposits through `placeDeposits`, loose refined units through
// `dropOnGround`, and void through `removeSquare`. So a zone adds no new kind
// of ore, only a recipe for where ore goes.
//
// **Deterministic from the seed**: a zone row carries kind and rectangle only;
// what grows in it is `streamFor(seed, index, ZONE_STREAM)`, so a log stays a
// few numbers long and replays to the same board. Spawn tables are the doc's,
// as written (assets/design-doc-as-of-october.txt, "Zones").

import { fileOf, rankOf, square } from "./board";
import {
  type DepositRow,
  dropOnGround,
  hasStructure,
  type IndustryState,
  makeUnit
} from "./industry/state";
import {
  COAL,
  CONCENTRATED,
  DIAMOND,
  ELECTRUM,
  FUEL,
  type MaterialLine,
  METAL,
  PURE,
  RICH,
  SPECIAL,
  STONE,
  THIN
} from "./materials";
import { hasCell, pieceAt, type Position } from "./position";
import { streamFor } from "./random";
import { IllegalCommandError, placeDeposits } from "./reduce";
import { ISLAND_LAYOUT_COUNT, islandGround } from "./islands";
import { removeSquare } from "./removeSquare";
import { RULES } from "./rules";
import {
  isZoneKind,
  ZONE_BATTLEFIELD,
  ZONE_BROKEN,
  ZONE_CANYON,
  ZONE_DEPOSIT,
  ZONE_DIAMOND,
  ZONE_FIELD,
  ZONE_GOLDEN,
  ZONE_QUARRY,
  ZONE_TREASURE,
  ZONE_VOID,
  type ZoneKind
} from "src/shared/zones";

export * from "src/shared/zones";

/**
 * The zone stream's salt. Past the deposits' block (81..127) and clear of
 * every salt in the repo (7, 11, 13, 31, 41, 51, 61–79); squares as salts are
 * hundreds of thousands, so they cannot meet it.
 */
export const ZONE_STREAM = 137;

/**
 * A zone as a command carries it: kind, south-west corner, width, height, and
 * for Broken Lands an island layout (§31 D3, `islands.ts`) — absent is D2's
 * default grid, so an older row grows as it did.
 */
export type ZoneRow = readonly [
  kind: number,
  file: number,
  rank: number,
  width: number,
  height: number,
  layout?: number
];

/** The longest side any zone may have — Broken Lands and the long canyon. */
export const ZONE_MAX_SIDE = 20;

/** Tier odds, indexed PURE / THIN / CONCENTRATED / RICH (by id, §33 M1). */
type Odds = readonly [number, number, number, number];
const QUARRY_ODDS: Odds = [1, 0, 0, 0];
/** "thin-rich-concentrated": D1's generated-tile odds. */
const TRC: Odds = [0, ...(RULES.depositTierOdds as [number, number, number])];
const THIN_RICH: Odds = [0, 0.5, 0, 0.5];
/** "highly weighted to rich" — the Deposit Zone's. */
const RICH_HEAVY: Odds = [0, 0.15, 0.15, 0.7];

/** One line of a spawn table: so many tiles of this material at these odds. */
interface Spawn {
  line: MaterialLine;
  grade: number;
  count: readonly [min: number, max: number];
  odds: Odds;
}

const quarry = (min: number, max: number): Spawn => ({ line: STONE, grade: 0, count: [min, max], odds: QUARRY_ODDS });
const iron = (min: number, max: number, odds: Odds): Spawn => ({ line: METAL, grade: 0, count: [min, max], odds });
const coal = (min: number, max: number, odds: Odds): Spawn => ({ line: FUEL, grade: COAL, count: [min, max], odds });
const electrum = (min: number, max: number): Spawn => ({ line: SPECIAL, grade: ELECTRUM, count: [min, max], odds: TRC });
const diamond = (min: number, max: number): Spawn => ({ line: SPECIAL, grade: DIAMOND, count: [min, max], odds: TRC });

export interface ZoneSpec {
  /** What a player reads. */
  name: string;
  /** The doc's size, `[width, height]` — what D3's generator places. */
  size: readonly [number, number];
  /** Tiles, in table order. */
  spawns: readonly Spawn[];
  /** "precisely one concentrated deposit that is either iron or coal". */
  oneConcentrated?: boolean;
}

/** The doc's zones. A table, so a kind cannot be placeable and unnamed. */
export const ZONE_SPECS: Record<ZoneKind, ZoneSpec> = {
  [ZONE_FIELD]: { name: "Field", size: [5, 5], spawns: [] },
  [ZONE_BATTLEFIELD]: { name: "Battlefield", size: [10, 10], spawns: [] },
  [ZONE_QUARRY]: {
    name: "Quarry Zone",
    size: [5, 5],
    spawns: [quarry(3, 5), coal(1, 3, TRC), iron(1, 2, THIN_RICH)]
  },
  [ZONE_DEPOSIT]: {
    name: "Deposit Zone",
    size: [5, 5],
    spawns: [quarry(1, 2), coal(2, 4, RICH_HEAVY), iron(2, 4, RICH_HEAVY)]
  },
  [ZONE_GOLDEN]: { name: "Golden Zone", size: [3, 3], spawns: [electrum(3, 5)], oneConcentrated: true },
  [ZONE_DIAMOND]: { name: "Diamond Zone", size: [2, 2], spawns: [diamond(1, 2)], oneConcentrated: true },
  [ZONE_VOID]: { name: "Void", size: [3, 3], spawns: [] },
  // Counts here are the 5×20 canyon's; a smaller one scales them by floor area.
  [ZONE_CANYON]: {
    name: "Canyon",
    size: [7, 20],
    spawns: [quarry(1, 15), iron(1, 15, TRC), electrum(0, 10), diamond(0, 6)]
  },
  [ZONE_BROKEN]: { name: "Broken Lands", size: [20, 20], spawns: [] },
  [ZONE_TREASURE]: { name: "Treasure Room", size: [2, 2], spawns: [] }
};

/** Why a row cannot be placed, or null. */
export function zoneFault(row: ZoneRow): string | null {
  const [kind, file, rank, width, height] = row;
  if (!isZoneKind(kind)) return `unknown zone kind ${String(kind)}`;
  for (const n of [file, rank, width, height]) {
    if (!Number.isInteger(n)) return "zone coordinates must be integers";
  }
  if (width < 1 || height < 1 || width > ZONE_MAX_SIDE || height > ZONE_MAX_SIDE) {
    return `a zone is 1 to ${ZONE_MAX_SIDE} squares a side`;
  }
  if (kind === ZONE_CANYON && Math.min(width, height) < 3) return "a canyon needs a wall each side of its floor";
  const layout = row[5];
  if (layout !== undefined) {
    if (kind !== ZONE_BROKEN) return "only Broken Lands take an island layout";
    if (!Number.isInteger(layout) || layout < 0 || layout >= ISLAND_LAYOUT_COUNT) {
      return `unknown island layout ${String(layout)}`;
    }
  }
  return null;
}

/** What one zone puts down. Squares ascending within each list. */
export interface ZoneYield {
  /** Labelled squares and their kind — a sub-zone's own label wins. */
  labels: [square: number, kind: ZoneKind][];
  /** Squares the zone takes off the board. */
  voids: number[];
  deposits: DepositRow[];
  /** Refined units lying loose, `[square, unit]`. */
  loose: [square: number, unit: number][];
}

/** A rectangle's squares, ascending. */
export function rectSquares(file: number, rank: number, width: number, height: number): number[] {
  const out: number[] = [];
  for (let r = rank; r < rank + height; r++) for (let f = file; f < file + width; f++) out.push(square(f, r));
  return out.sort((a, b) => a - b);
}

const between = (roll: () => number, min: number, max: number): number =>
  min + Math.floor(roll() * (max - min + 1));

function tierOf(roll: () => number, odds: Odds): number {
  let r = roll();
  for (const tier of [PURE, THIN, CONCENTRATED, RICH]) {
    if (r < odds[tier]) return tier;
    r -= odds[tier];
  }
  return [PURE, THIN, CONCENTRATED, RICH].filter((t) => odds[t] > 0).pop() ?? PURE;
}

/** Units in one tile: a special keeps its flat amount (D1), anything else the vein's. */
const tileAmount = (line: MaterialLine): number =>
  line === SPECIAL ? RULES.depositSpecialAmount : RULES.depositAmount;

/** Take a square out of `open` at random; -1 when there is none left. */
function draw(open: number[], roll: () => number): number {
  if (open.length === 0) return -1;
  return open.splice(Math.floor(roll() * open.length), 1)[0];
}

function spawnTiles(spawns: readonly Spawn[], open: number[], roll: () => number, scale = 1): DepositRow[] {
  const out: DepositRow[] = [];
  for (const spawn of spawns) {
    const max = spawn.count[0] + Math.round((spawn.count[1] - spawn.count[0]) * scale);
    const count = between(roll, spawn.count[0], max);
    for (let i = 0; i < count; i++) {
      const sq = draw(open, roll);
      if (sq < 0) return out;
      out.push([sq, spawn.line, spawn.grade, tileAmount(spawn.line), tierOf(roll, spawn.odds)]);
    }
  }
  return out;
}

/** "precisely one concentrated deposit that is either iron or coal". */
function oneConcentrated(open: number[], roll: () => number): DepositRow[] {
  const line = roll() < 0.5 ? METAL : FUEL;
  const sq = draw(open, roll);
  return sq < 0 ? [] : [[sq, line, line === FUEL ? COAL : 0, RULES.depositAmount, CONCENTRATED]];
}

/** 12–24 units, about three electrum to a diamond, dealt round the room. */
function treasure(squares: readonly number[], roll: () => number): [number, number][] {
  const total = between(roll, 12, 24);
  const out: [number, number][] = [];
  for (let i = 0; i < total; i++) {
    const grade = roll() < 0.75 ? ELECTRUM : DIAMOND;
    out.push([squares[i % squares.length], makeUnit(grade, 0, SPECIAL)]);
  }
  return out;
}

/** Roll a 2×2 Treasure Room somewhere in `open` (a rectangle's squares). */
function treasureRoom(
  file: number,
  rank: number,
  width: number,
  height: number,
  roll: () => number,
  yieldOut: ZoneYield,
  taken: Set<number>
): void {
  if (width < 2 || height < 2) return;
  const f = file + Math.floor(roll() * (width - 1));
  const r = rank + Math.floor(roll() * (height - 1));
  const room = rectSquares(f, r, 2, 2);
  if (room.some((sq) => taken.has(sq))) return;
  for (const sq of room) {
    taken.add(sq);
    yieldOut.labels.push([sq, ZONE_TREASURE]);
  }
  yieldOut.loose.push(...treasure(room, roll));
}

/** A small scatter of refined metal and stone, one or two units a spot. */
function scatter(open: number[], spots: number, roll: () => number, minGrade: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < spots; i++) {
    const sq = draw(open, roll);
    if (sq < 0) break;
    const line = roll() < 0.6 ? METAL : STONE;
    const units = between(roll, 1, 2);
    for (let u = 0; u < units; u++) out.push([sq, makeUnit(between(roll, minGrade, 2), 0, line)]);
  }
  return out;
}

/**
 * What a zone grows, as a pure function of its row and a stream. No board is
 * read: the caller filters against the ground (`applyZone`).
 */
export function zoneYield(row: ZoneRow, roll: () => number): ZoneYield {
  const [kind, file, rank, width, height] = row as [ZoneKind, number, number, number, number];
  const all = rectSquares(file, rank, width, height);
  const out: ZoneYield = { labels: [], voids: [], deposits: [], loose: [] };
  const spec = ZONE_SPECS[kind];

  if (kind === ZONE_VOID) {
    out.voids = all;
    return out;
  }

  const taken = new Set<number>();
  if (kind === ZONE_CANYON) {
    // The two long edges are the walls — void until S4's unbreakable walls
    // land; a jumper passes either. The ends stay open: the entrances.
    const along = width >= height;
    for (const sq of all) {
      const edge = along
        ? rankOf(sq) === rank || rankOf(sq) === rank + height - 1
        : fileOf(sq) === file || fileOf(sq) === file + width - 1;
      if (edge) {
        out.voids.push(sq);
        taken.add(sq);
      }
    }
  }
  const layout = row[5];
  if (kind === ZONE_BROKEN && layout !== undefined) {
    // A map-maker's layout (§31 D3): void wherever the mask is.
    for (const sq of all) {
      if (!islandGround(layout, fileOf(sq) - file, rankOf(sq) - rank)) {
        out.voids.push(sq);
        taken.add(sq);
      }
    }
  } else if (kind === ZONE_BROKEN) {
    // Islands of four by four, one-square channels of void between them.
    for (const sq of all) {
      if ((fileOf(sq) - file) % 5 === 4 || (rankOf(sq) - rank) % 5 === 4) {
        out.voids.push(sq);
        taken.add(sq);
      }
    }
  }

  // Sub-zones first, so a deposit is never rolled into a treasure room.
  if (kind === ZONE_BATTLEFIELD && roll() < 0.5) {
    treasureRoom(file, rank, width, height, roll, out, taken);
  }
  if (kind === ZONE_TREASURE) {
    for (const sq of all) {
      taken.add(sq);
      out.labels.push([sq, ZONE_TREASURE]);
    }
    out.loose.push(...treasure(all, roll));
  }

  const open = all.filter((sq) => !taken.has(sq));
  for (const sq of open) out.labels.push([sq, kind]);

  if (kind === ZONE_BATTLEFIELD) {
    out.loose.push(...scatter(open, Math.max(2, Math.round(open.length / 12)), roll, 0));
  } else if (kind === ZONE_BROKEN && layout !== undefined) {
    layoutIslands(open, roll, out);
  } else if (kind === ZONE_BROKEN) {
    brokenIslands(file, rank, width, height, open, roll, out, taken);
  } else {
    const floor = kind === ZONE_CANYON ? open.length : 0;
    const scale = kind === ZONE_CANYON ? Math.min(1, floor / 100) : 1;
    if (spec.oneConcentrated) out.deposits.push(...oneConcentrated(open, roll));
    out.deposits.push(...spawnTiles(spec.spawns, open, roll, scale));
  }
  out.labels.sort((a, b) => a[0] - b[0]);
  out.deposits.sort((a, b) => a[0] - b[0]);
  return out;
}

/**
 * Broken Lands without a map-maker's layout (a row's sixth field picks one —
 * `layoutIslands`): each island
 * rolls a vein of one or two tiles, a cluster of refined material, or nothing;
 * one island may hold a Treasure Room.
 */
function brokenIslands(
  file: number,
  rank: number,
  width: number,
  height: number,
  open: number[],
  roll: () => number,
  out: ZoneYield,
  taken: Set<number>
): void {
  const room = roll() < 0.5;
  const islands: { f: number; r: number; w: number; h: number }[] = [];
  for (let r = rank; r < rank + height; r += 5) {
    for (let f = file; f < file + width; f += 5) {
      islands.push({ f, r, w: Math.min(4, file + width - f), h: Math.min(4, rank + height - r) });
    }
  }
  const roomAt = room && islands.length > 0 ? Math.floor(roll() * islands.length) : -1;
  islands.forEach((island, i) => {
    if (i === roomAt) {
      treasureRoom(island.f, island.r, island.w, island.h, roll, out, taken);
      // A room relabels its squares; drop the island's own label there.
      out.labels = out.labels.filter(([sq, kind]) => kind === ZONE_TREASURE || !taken.has(sq));
    }
    const here = open.filter(
      (sq) =>
        !taken.has(sq) &&
        fileOf(sq) >= island.f &&
        fileOf(sq) < island.f + island.w &&
        rankOf(sq) >= island.r &&
        rankOf(sq) < island.r + island.h
    );
    const pick = roll();
    if (pick < 0.4) {
      const lines: Spawn[] = [quarry(1, 2), iron(1, 2, TRC), coal(1, 2, TRC), electrum(1, 1)];
      out.deposits.push(...spawnTiles([lines[Math.floor(roll() * lines.length)]], here, roll));
    } else if (pick < 0.7) {
      out.loose.push(...scatter(here, between(roll, 1, 2), roll, 1));
    }
  });
}

/** `sq`'s four orthogonal neighbours. */
const besideOf = (sq: number): number[] => [
  square(fileOf(sq) + 1, rankOf(sq)),
  square(fileOf(sq) - 1, rankOf(sq)),
  square(fileOf(sq), rankOf(sq) + 1),
  square(fileOf(sq), rankOf(sq) - 1)
];

/** `open` as 4-connected islands, each ascending, in order of their lowest square. */
function islandsOf(open: readonly number[]): number[][] {
  const left = new Set(open);
  const out: number[][] = [];
  for (const start of open) {
    if (!left.delete(start)) continue;
    const island = [start];
    for (let i = 0; i < island.length; i++) {
      for (const next of besideOf(island[i])) if (left.delete(next)) island.push(next);
    }
    out.push(island.sort((a, b) => a - b));
  }
  return out;
}

/**
 * Broken Lands on a map-maker's layout (§31 D3). Each island — a 4-connected
 * piece of the mask — rolls as the default grid's do: a vein of one or two
 * tiles, a refined cluster, or nothing. **Iron brings its coal** (§31 M2): an
 * iron vein lays a coal tile on the same island. One island may hold a
 * Treasure Room, on a 2×2 of its own ground.
 */
function layoutIslands(open: readonly number[], roll: () => number, out: ZoneYield): void {
  const islands = islandsOf(open);
  const roomAt = islands.length > 0 && roll() < 0.5 ? Math.floor(roll() * islands.length) : -1;
  const room = new Set<number>();
  if (roomAt >= 0) {
    const island = new Set(islands[roomAt]);
    const anchors = islands[roomAt].filter((sq) =>
      [
        square(fileOf(sq) + 1, rankOf(sq)),
        square(fileOf(sq), rankOf(sq) + 1),
        square(fileOf(sq) + 1, rankOf(sq) + 1)
      ].every((n) => island.has(n))
    );
    if (anchors.length > 0) {
      const at = anchors[Math.floor(roll() * anchors.length)];
      const squares = rectSquares(fileOf(at), rankOf(at), 2, 2);
      for (const sq of squares) room.add(sq);
      out.labels = out.labels.map(([sq, kind]): [number, ZoneKind] => [sq, room.has(sq) ? ZONE_TREASURE : kind]);
      out.loose.push(...treasure(squares, roll));
    }
  }
  for (const island of islands) {
    const here = island.filter((sq) => !room.has(sq));
    const pick = roll();
    if (pick < 0.4) {
      const lines: Spawn[] = [quarry(1, 2), iron(1, 2, TRC), coal(1, 2, TRC), electrum(1, 1)];
      const line = lines[Math.floor(roll() * lines.length)];
      out.deposits.push(...spawnTiles(line.line === METAL ? [line, coal(1, 1, TRC)] : [line], here, roll));
    } else if (pick < 0.7) {
      out.loose.push(...scatter(here, between(roll, 1, 2), roll, 1));
    }
  }
}

/**
 * The squares of a row a piece can stand on, by geometry alone — no stream:
 * a canyon less its walls, Broken Lands less their channels, nothing for a
 * void. What the generator joins a zone to the board by (§31 D3).
 */
export function zoneFloor(row: ZoneRow): number[] {
  const [kind, file, rank, width, height, layout] = row;
  const all = rectSquares(file, rank, width, height);
  if (kind === ZONE_VOID) return [];
  if (kind === ZONE_CANYON) {
    const along = width >= height;
    return all.filter((sq) =>
      along
        ? rankOf(sq) !== rank && rankOf(sq) !== rank + height - 1
        : fileOf(sq) !== file && fileOf(sq) !== file + width - 1
    );
  }
  if (kind === ZONE_BROKEN) {
    return all.filter((sq) =>
      layout !== undefined
        ? islandGround(layout, fileOf(sq) - file, rankOf(sq) - rank)
        : (fileOf(sq) - file) % 5 !== 4 && (rankOf(sq) - rank) % 5 !== 4
    );
  }
  return all;
}

/** Do any of `squares` touch `ground` orthogonally (or lie on it)? */
export const touches = (squares: readonly number[], ground: ReadonlySet<number>): boolean =>
  squares.some((sq) => ground.has(sq) || besideOf(sq).some((n) => ground.has(n)));

/**
 * Put one zone's yield on the board. **Voids first** (a piece standing there
 * keeps its ground — a zone never deletes an army), then labels on whatever is
 * still a cell, then deposits through `placeDeposits` (its own skips), then
 * loose units on cells no building stands on.
 */
export function applyZone(position: Position, industry: IndustryState, y: ZoneYield): void {
  for (const sq of y.voids) {
    if (hasCell(position, sq) && pieceAt(position, sq) === 0 && !hasStructure(industry, sq)) {
      removeSquare(position, industry, sq);
    }
  }
  const labelled = y.labels.filter(([sq]) => hasCell(position, sq));
  if (labelled.length > 0) {
    industry.zone ??= new Map();
    for (const [sq, kind] of labelled) industry.zone.set(sq, kind);
  }
  placeDeposits(position, industry, y.deposits);
  for (const [sq, unit] of y.loose) {
    if (hasCell(position, sq) && !hasStructure(industry, sq)) dropOnGround(industry, sq, unit);
  }
}

/**
 * Lay a command's zones, each grown off its own stream (`ZONE_STREAM`,
 * indexed by row). **Refused, never defaulted**, for a row this build cannot
 * read — the map rule.
 */
export function placeZones(
  position: Position,
  industry: IndustryState,
  rows: readonly ZoneRow[],
  seed: number
): void {
  for (const row of rows) {
    const fault = zoneFault(row);
    if (fault) throw new IllegalCommandError(`bad zone: ${fault}`);
  }
  rows.forEach((row, i) => applyZone(position, industry, zoneYield(row, streamFor(seed, i, ZONE_STREAM))));
}

/** The zone a square lies in, or 0. */
export const zoneAt = (industry: IndustryState, sq: number): number => industry.zone?.get(sq) ?? 0;

/** Turn a yield half a turn about the centre of the classic board. */
export function rotateYield(y: ZoneYield): ZoneYield {
  const turn = (sq: number): number => square(7 - fileOf(sq), 7 - rankOf(sq));
  return {
    labels: y.labels.map(([sq, kind]): [number, ZoneKind] => [turn(sq), kind]).sort((a, b) => a[0] - b[0]),
    voids: y.voids.map(turn).sort((a, b) => a - b),
    deposits: y.deposits
      .map(([sq, line, grade, amount, tier]): DepositRow => [turn(sq), line, grade, amount, tier])
      .sort((a, b) => a[0] - b[0]),
    loose: y.loose.map(([sq, unit]): [number, number] => [turn(sq), unit])
  };
}

/**
 * A map's slot: a rectangle on White's half and a pool of kinds. The reducer
 * draws a kind and grows it, then lays the same yield turned half about for
 * Black — a seed gives a board, and the board is fair.
 */
export interface ZoneSlot {
  at: readonly [file: number, rank: number, width: number, height: number];
  pool: readonly ZoneKind[];
}

/**
 * Lay a map's slots off the seed. A kind smaller than its slot (a Golden Zone
 * in a 5×4 flank) sits at a drawn offset inside it, the rest a Field.
 */
export function placeMapZones(
  position: Position,
  industry: IndustryState,
  slots: readonly ZoneSlot[],
  seed: number
): void {
  slots.forEach((slot, i) => {
    // Its own salt beside `placeZones`', so a map and a command's rows never
    // share a stream.
    const roll = streamFor(seed, i, ZONE_STREAM + 1);
    const [file, rank, width, height] = slot.at;
    const kind = slot.pool[Math.floor(roll() * slot.pool.length)];
    const [w, h] = ZONE_SPECS[kind].size.map((n, axis) => Math.min(n, axis === 0 ? width : height));
    const f = file + Math.floor(roll() * (width - w + 1));
    const r = rank + Math.floor(roll() * (height - h + 1));
    const inner = zoneYield([kind, f, r, w, h], roll);
    const field = w < width || h < height ? zoneYield([ZONE_FIELD, file, rank, width, height], roll) : null;
    const ownLabel = new Set(inner.labels.map(([sq]) => sq));
    const y: ZoneYield = {
      ...inner,
      labels: [...inner.labels, ...(field?.labels.filter(([sq]) => !ownLabel.has(sq)) ?? [])].sort(
        (a, b) => a[0] - b[0]
      )
    };
    applyZone(position, industry, y);
    applyZone(position, industry, rotateYield(y));
  });
}
